import { userQuery, useUsers } from "@common/api/users"
import type { UserPublic } from "@common/client"
import { UserInitials } from "@common/components/shared/user-initials"
import { useSuspenseQuery } from "@tanstack/react-query"
import {
  createFileRoute,
  getRouteApi,
  Outlet,
  useNavigate,
} from "@tanstack/react-router"
import { DetailPanel } from "@/components/layout/detail-panel"
import { MetaItem, MetaList } from "@/components/page/meta"
import { PaneStepper, PaneTitle } from "@/components/page/pane"
import useCustomToast from "@/hooks/use-custom-toast"
import { MemberDetailsForm } from "../../-components/form/member-details-form"
import { displayName, initialsName } from "../../-lib/display-name"
import { memberListParams } from "../../-lib/list-params"

const membersRoute = getRouteApi(
  "/_layout/settings/_panel/workspace/members/_list",
)

const IDENTITY = "flex shrink-0 items-start gap-3 border-b px-4 pt-3.5"

export const Route = createFileRoute(
  "/_layout/settings/_panel/workspace/members/_list/$userId",
)({
  staticData: { detailSidebar: true },
  loader: ({ context: { queryClient }, params: { userId } }) =>
    queryClient.ensureQueryData(userQuery({ user_id: userId })),
  component: MemberPanel,
})

function MemberPanel(): React.ReactElement {
  const { userId } = Route.useParams()
  const { data: user } = useSuspenseQuery(userQuery({ user_id: userId }))
  const navigate = useNavigate()
  const showToast = useCustomToast()
  const close = () =>
    navigate({ to: "/settings/workspace/members", search: true })

  return (
    <DetailPanel
      title={displayName(user)}
      heading={<MemberNavigation userId={userId} />}
      onClose={close}
    >
      <div className="flex h-full flex-col">
        <Identity user={user} />
        <MemberDetailsForm
          key={user.id}
          user={user}
          onSave={(saved) =>
            showToast("Saved", `${displayName(saved)} updated.`, "success")
          }
        />
      </div>
      <Outlet />
    </DetailPanel>
  )
}

function MemberNavigation({
  userId,
}: {
  userId: string
}): React.ReactElement | null {
  const filters = membersRoute.useSearch()
  const { users, totalCount, hasNextPage, fetchNextPage, isFetchingNextPage } =
    useUsers(memberListParams(filters))
  const navigate = useNavigate()
  const index = users.findIndex((user) => user.id === userId)
  const previous = index > 0 ? users[index - 1] : undefined
  const next = index >= 0 ? users[index + 1] : undefined

  const show = (id: string) =>
    navigate({
      to: "/settings/workspace/members/$userId",
      params: { userId: id },
      search: true,
    })

  const showNext = () => {
    if (next) return show(next.id)
    return fetchNextPage().then((result) => {
      const first = result.data?.pages.at(-1)?.data[0]
      if (first) return show(first.id)
    })
  }

  return (
    <PaneStepper
      noun="member"
      index={index}
      total={totalCount}
      onPrevious={previous ? () => show(previous.id) : null}
      onNext={(!next && !hasNextPage) || isFetchingNextPage ? null : showNext}
    />
  )
}

function Identity({ user }: { user: UserPublic }): React.ReactElement {
  return (
    <div className={IDENTITY}>
      <UserInitials
        user={initialsName(user)}
        title={null}
        className="size-9 text-body-semibold"
      />
      <div className="min-w-0">
        <PaneTitle title={displayName(user)}>
          <MetaList>
            <MetaItem>{user.email ?? user.username}</MetaItem>
          </MetaList>
        </PaneTitle>
      </div>
    </div>
  )
}
